import { APIClient } from '@innerhive-internal/innerhive-api-client';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { Logger } from '../support/Logger';

// Refreshed this long before it actually expires, so a token is never handed to an <img> moments
// before it stops working — the image would simply fail to load with nothing to retry it.
const REFRESH_MARGIN_MS = 60 * 1000;

// Holds the short-lived credential the media routes accept, and trades the session for a new one
// when it is close to expiring.
//
// The point of the trade is that this token reaches only the media routes. It travels in a url,
// because <img> and CSS background-image cannot set headers, and urls end up in browser history,
// referrer headers, access logs, and Mixpanel session replay — replay masks text only, so it
// records an image src and an inline background-image url in full. Note the scope is the routes, not the one image it was minted
// for: it reaches whatever media this principal could already read, attachments included. What it
// cannot do is reach anything else, which is why it is traded for rather than reusing the account's
// access token, and why it lasts minutes.
//
// One in-flight request is shared: a care map paints many faces at once, and each would otherwise
// mint its own token.
export class MediaTokenProvider {

    static inject = () => [APIClient, BufferedEventBus, Logger];
    constructor(
        private apiClient: APIClient,
        private events: BufferedEventBus,
        private log: Logger
    ) {}

    // Signing out reloads the page, which would discard this anyway — but a cached credential for
    // the previous principal must not depend on that for its removal, and the reload is a detail
    // of how logout happens rather than a guarantee.
    init = () => {
        this.events.on('AuthService.signing-out', () => {
            this.clear();
        });
    };

    private token?: string;
    private expiresAtMs = 0;
    private inFlight?: Promise<string | undefined>;
    private generation = 0;

    // Undefined when a token cannot be obtained, which callers treat as a FAILURE rather than as
    // permission to read the object another way: buildAuthorizedMediaUrl throws when a download
    // link was offered and no token arrived. It used to fall back to the stored bucket url, which
    // turned every failed mint into a silent unaudited read; the degradation now is a missing image
    // and a Sentry error, which is the honest outcome.
    get = async (): Promise<string | undefined> => {
        if (this.token && Date.now() < this.expiresAtMs - REFRESH_MARGIN_MS) {
            return this.token;
        }

        if (!this.inFlight) {
            // The cleanup identifies itself before clearing, because clear() also drops inFlight:
            // without the check, an abandoned mint settling after sign-out would clear whatever the
            // NEXT principal had started, and the care map repainting for them would mint once per
            // face — the single-flight guarantee this exists for, lost exactly when it matters.
            const attempt: Promise<string | undefined> = this.fetch().finally(() => {
                if (this.inFlight === attempt) {
                    this.inFlight = undefined;
                }
            });
            this.inFlight = attempt;
        }
        return await this.inFlight;
    };

    // Dropped on sign-out so a later principal cannot reuse the previous one's credential. The
    // generation bump is what makes that hold when a mint is already in flight: without it the
    // request settles after this ran and writes the signed-out principal's token back into the
    // cache, which is exactly the outcome the removal is not supposed to depend on a reload for.
    clear = () => {
        this.generation++;
        this.token = undefined;
        this.expiresAtMs = 0;

        // Dropped as well as invalidated. The generation bump stops the in-flight request writing
        // to the cache, but a caller arriving before it settles would otherwise join it and receive
        // the undefined it now returns — which resolves to a refused read for whoever comes next.
        // The principal that replaces the one signing out is usually an anonymous viewer, so the
        // token being dropped rather than lingering is what stops it being reused across identities.
        this.inFlight = undefined;
    };

    private fetch = async (): Promise<string | undefined> => {
        const generation = this.generation;
        try {
            const entry = await this.apiClient.entry();
            if (!entry.links?.mediaToken) {
                // An API older than the media routes. Nothing to trade for.
                return undefined;
            }

            const { token, expiresAt } = await this.apiClient.post(entry.links.mediaToken, {});

            if (generation !== this.generation) {
                // Signed out while this was in flight. The token belongs to a principal who is
                // gone, so it is neither cached nor returned.
                return undefined;
            }

            this.token = token;
            this.expiresAtMs = new Date(expiresAt).valueOf();
            return token;
        } catch (e) {
            this.log.error(e as Error, { context: 'MediaTokenProvider could not mint a media token' });
            return undefined;
        }
    };
}
